import React, { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';

const RED = '#D01919';
const GOLD = '#C9A24B';

const norm = (s) => (s || '').trim().replace(/\s+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const initials = (n) => (n || '').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

function LeaderCard({ l, i }) {
  return (
    <div className="rounded-2xl border border-[#2a2a2a] bg-[#141414] p-5 h-full flex gap-4 hover:border-[#D01919]/60 transition-colors" data-testid={`the100-leader-${i}`}>
      {l.image ? <img src={l.image} alt={l.name} className="w-14 h-14 rounded-full object-cover shrink-0 grayscale" />
        : <div className="w-14 h-14 rounded-full shrink-0 flex items-center justify-center font-display text-lg text-white" style={{ background: 'linear-gradient(160deg, #3a3a3a, #161616)', boxShadow: `inset 0 0 0 1px ${GOLD}55` }}>{initials(l.name)}</div>}
      <div className="min-w-0">
        <p className="font-display text-lg text-white leading-snug">{l.name}</p>
        {l.designation || l.organisation ? <p className="text-sm text-white/60 mt-0.5">{[l.designation, l.organisation].filter(Boolean).join(', ')}</p> : null}
        {l.sector ? <p className="text-[0.68rem] uppercase tracking-wider mt-2" style={{ color: RED }}>{l.sector}</p> : null}
        {l.citation ? <p className="text-sm text-white/50 mt-2 leading-relaxed">{l.citation}</p> : null}
      </div>
    </div>
  );
}

export default function LeadersGrid({ leaders }) {
  const [q, setQ] = useState('');
  const [sector, setSector] = useState('All');
  const sectors = useMemo(() => ['All', ...Array.from(new Set(leaders.map((l) => norm(l.sector)).filter(Boolean))).sort()], [leaders]);
  const active = sectors.includes(sector) ? sector : 'All';
  const term = q.trim().toLowerCase();
  const shown = leaders.filter((l) => (active === 'All' || norm(l.sector) === active)
    && (!term || [l.name, l.designation, l.organisation, l.sector].join(' ').toLowerCase().includes(term)));
  const chip = (s) => `px-3.5 py-1.5 rounded-full text-sm border transition-colors ${active === s ? 'bg-[#D01919] border-[#D01919] text-white' : 'border-[#333333] text-white/70 hover:border-white/50'}`;

  return (
    <section className="section-pad py-14" data-testid="the100-leaders">
      <div className="max-w-[1500px] mx-auto">
        <p className="eyebrow mb-3" style={{ color: RED }}>The list</p>
        <h2 className="font-display text-4xl md:text-5xl text-white">Meet The100</h2>
        <div className="mt-8 flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="relative w-full lg:max-w-md">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, organisation or sector"
              className="w-full bg-[#141414] border border-[#2a2a2a] rounded-full pl-11 pr-10 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-[#D01919]" data-testid="the100-search" />
            {q ? <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white" data-testid="the100-search-clear" aria-label="Clear search"><X size={16} /></button> : null}
          </div>
          {sectors.length > 2 ? (
            <div className="flex flex-wrap gap-2" data-testid="the100-sector-filter">
              {sectors.map((s) => <button key={s} onClick={() => setSector(s)} className={chip(s)} data-testid={`the100-sector-${s.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{s}</button>)}
            </div>
          ) : null}
        </div>
        <p className="text-sm text-white/40 mt-5" data-testid="the100-count">Showing {shown.length} of {leaders.length} leaders</p>
        {shown.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-5">
            {shown.map((l, i) => <LeaderCard key={l.id || i} l={l} i={i} />)}
          </div>
        ) : <p className="text-white/50 mt-8" data-testid="the100-no-results">No leaders match your search.</p>}
      </div>
    </section>
  );
}
